import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/auth-context';
import { useMarketingReport } from '@/hooks/useMarketingReport';
import { isGa4Available, MarketingGa4Unavailable } from '@/lib/api/marketing-report-service';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { MarketingMetricsStrip } from '@/components/marketing/MarketingMetricsStrip';
import { MarketingDailyChart } from '@/components/marketing/MarketingDailyChart';
import { MarketingPagesTable } from '@/components/marketing/MarketingPagesTable';
import { MarketingSourceFunnelTable } from '@/components/marketing/MarketingSourceFunnelTable';
import { MarketingLeadsTable } from '@/components/marketing/MarketingLeadsTable';
import { RefreshCw, AlertCircle } from 'lucide-react';

type Preset = '7' | '30' | '90' | 'custom';

function isoDate(d: Date): string {
  // Use local date parts — toISOString() is UTC and shows the wrong day for users east of UTC
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0'),
  ].join('-');
}

function presetRange(preset: Preset): { start: string; end: string } {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - (preset === 'custom' ? 30 : parseInt(preset, 10)) + 1);
  return { start: isoDate(start), end: isoDate(end) };
}

/**
 * Shown IN PLACE of the GA4 widgets when the backend could not query Google
 * Analytics (#5480). The report used to 500 as a whole — CRM leads included — over a
 * property that was merely unconfigured, so the page was a raw error string for six
 * days. This says what is wrong and where to fix it; the leads still render below.
 */
function Ga4UnavailableBanner({ ga4 }: { ga4: MarketingGa4Unavailable }) {
  const notConfigured = ga4.reason === 'not_configured';
  return (
    <Alert variant="destructive" data-testid="marketing-ga4-unavailable">
      <AlertCircle className="h-4 w-4" />
      <AlertTitle>
        {notConfigured
          ? 'Google Analytics property not configured'
          : 'Google Analytics returned an error'}
      </AlertTitle>
      <AlertDescription className="space-y-2">
        <p>
          {notConfigured ? (
            <>
              No Analytics property is configured for the <strong>pagemind</strong> site,
              so sessions, traffic sources and page figures cannot be shown.
            </>
          ) : (
            <>Google Analytics rejected the request, so sessions, traffic sources and page figures cannot be shown.</>
          )}{' '}
          Website leads from the CRM are unaffected and are listed below.
        </p>
        <p>
          To fix it: select the <strong>pagemind</strong> site in the site selector, open{' '}
          <Link to="/settings/integrations" className="font-medium underline underline-offset-2">
            Settings → Integrations
          </Link>{' '}
          and pick the Analytics property on the <strong>Configure</strong> tab
          {notConfigured ? '.' : ', then check that the selected property is the right one and that access to it has not been revoked.'}
        </p>
        {/* The backend's own message — the ambiguous-property case, for instance,
            names the candidate properties here, and an API error names Google's reason. */}
        <p className="font-mono text-xs break-words">{ga4.detail}</p>
      </AlertDescription>
    </Alert>
  );
}

export default function MarketingReport() {
  const { user } = useAuth();
  const { data, loading, error, load } = useMarketingReport();

  const [preset, setPreset] = useState<Preset>('30');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const isSuperAdmin = user?.role === 'super_admin';

  // Initial load on mount with default preset
  useEffect(() => {
    if (!isSuperAdmin) return;
    const { start, end } = presetRange('30');
    load(start, end);
  }, [isSuperAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  function handlePresetChange(value: string) {
    const p = value as Preset;
    setPreset(p);
    if (p !== 'custom') {
      const { start, end } = presetRange(p);
      load(start, end);
    }
  }

  function handleCustomSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!customStart || !customEnd) return;
    load(customStart, customEnd);
  }

  function handleRefresh() {
    if (preset === 'custom') {
      if (customStart && customEnd) load(customStart, customEnd, true);
    } else {
      const { start, end } = presetRange(preset);
      load(start, end, true);
    }
  }

  if (!isSuperAdmin) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>This page is only accessible to super admins.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Marketing Site Report</h1>
          {data && (
            <p className="text-sm text-muted-foreground mt-0.5">
              {data.range.start} – {data.range.end}
              {data.cache.hit && (
                <span className="ml-2 text-xs">(cached {data.cache.ageSeconds}s ago)</span>
              )}
            </p>
          )}
        </div>

        {/* Date-range controls */}
        <div className="flex flex-wrap items-end gap-2">
          <Select value={preset} onValueChange={handlePresetChange}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="custom">Custom</SelectItem>
            </SelectContent>
          </Select>

          {preset === 'custom' && (
            <form onSubmit={handleCustomSubmit} className="flex items-end gap-2">
              <div className="flex flex-col gap-0.5">
                <label className="text-xs text-muted-foreground">Start</label>
                <input
                  type="date"
                  value={customStart}
                  onChange={e => setCustomStart(e.target.value)}
                  max={customEnd || isoDate(new Date())}
                  required
                  className="h-9 rounded-md border px-2 text-sm bg-background"
                />
              </div>
              <div className="flex flex-col gap-0.5">
                <label className="text-xs text-muted-foreground">End</label>
                <input
                  type="date"
                  value={customEnd}
                  onChange={e => setCustomEnd(e.target.value)}
                  min={customStart}
                  max={isoDate(new Date())}
                  required
                  className="h-9 rounded-md border px-2 text-sm bg-background"
                />
              </div>
              <Button type="submit" disabled={!customStart || !customEnd || loading} size="sm">
                Apply
              </Button>
            </form>
          )}

          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* GA4 truncation — figures are rendered but under-count, so say so plainly
          instead of letting partial data pass for exact. */}
      {data?.warnings && data.warnings.length > 0 && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <span className="font-medium">Some figures are incomplete.</span> Google
            Analytics returned more rows than were fetched, so the affected widgets
            under-count:
            <ul className="mt-1 list-disc pl-5">
              {data.warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Loading skeleton */}
      {loading && !data && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-20 rounded-lg bg-muted animate-pulse" />
            ))}
          </div>
          <div className="h-72 rounded-lg bg-muted animate-pulse" />
        </div>
      )}

      {/* Data — CRM-only when GA4 could not be queried (#5480): the banner takes the
          place of every GA4 widget rather than those widgets rendering zeros. */}
      {data && !isGa4Available(data) && (
        <div className="space-y-6">
          <Ga4UnavailableBanner ga4={data.ga4} />
          <MarketingLeadsTable leads={data.leads} />
        </div>
      )}
      {data && isGa4Available(data) && (
        <div className="space-y-6">
          <MarketingMetricsStrip kpis={data.kpis} />
          <MarketingDailyChart daily={data.daily} />
          {/* Leads sit directly under the chart: the chart's lead dots are the hook,
              and this is the table that says who they were. */}
          <MarketingLeadsTable leads={data.leads} />
          {/* No ?? fallbacks: the response shape is validated in fetchMarketingReport,
              so a missing section raises a visible error instead of rendering as empty. */}
          <MarketingSourceFunnelTable
            sourceFunnel={data.sourceFunnel}
            reached={data.contactFunnel.reached}
          />
          <MarketingPagesTable pages={data.pages} />
        </div>
      )}
    </div>
  );
}
